import type {
  PullResponse,
  PushResponse,
  ServerRow,
  TableChanges,
} from '@worker/sync/types'
import type { Table } from 'dexie'
import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import {
  getDeviceId,
  getLastPulledSeq,
  getSyncState,
  setLastPulledSeq,
  setSyncState,
} from '@/core/repository/syncStateRepository'
import { logger } from '@/core/logging/logger'
import { repairSlotDuplicates } from './repairSlots'
import {
  deserializeRow,
  LOCAL_TABLES,
  serializeRow,
  toLocalTable,
  toSqlTable,
  type LocalTable,
} from './serialize'

/**
 * The sync cycle: push, then pull.
 *
 * Pushing first means your own changes come back confirmed rather than being
 * clobbered by a stale pull.
 *
 * Three bugs are actively defended against here, all of which lose data
 * silently. Each is marked at the point it matters:
 *
 *   §4.1  clearing a dirty flag for an edit made mid-flight
 *   §6    a pull overwriting a newer unpushed local edit
 *   §2    paginating on updatedAt instead of server_seq
 */

/** Rows per push request. */
export const PUSH_CHUNK_SIZE = 200

export type SyncOutcome =
  | { status: 'ok'; pushed: number; pulled: number; repaired: number }
  | { status: 'skipped'; reason: 'offline' | 'in-flight' | 'disabled' | 'no-token' }
  | { status: 'error'; message: string; unauthorized: boolean }

export interface SyncTransport {
  push(body: { deviceId: string; changes: TableChanges }, token: string): Promise<PushResponse>
  pull(sinceSeq: number, deviceId: string, token: string): Promise<PullResponse>
}

/** A synced row, viewed generically. */
type AnyRow = Record<string, unknown> & { id: string; updatedAt: number; _dirty: 0 | 1 }

interface DirtyRow {
  table: LocalTable
  row: AnyRow
}

/**
 * The sync engine treats every table identically, but Dexie types each one
 * separately, so the union of ten table types has no callable `modify`. This
 * is the one place that erases the distinction, deliberately and in view.
 */
function tableOf(db: BlockyTimeDB, name: LocalTable): Table<AnyRow, string> {
  return db[name] as unknown as Table<AnyRow, string>
}

let inFlight = false

/** Exposed for tests, which must not inherit state between cases. */
export function resetSyncEngine(): void {
  inFlight = false
}

export async function sync(
  transport: SyncTransport,
  db: BlockyTimeDB = defaultDb,
): Promise<SyncOutcome> {
  // Claim the lock BEFORE the first await. Any await between testing the flag
  // and setting it is a race: two concurrent callers both see it clear, both
  // proceed, and they interleave pushes and cursor updates.
  if (inFlight) return { status: 'skipped', reason: 'in-flight' }
  inFlight = true

  try {
    const token = await getSyncState('syncToken', db)
    if (!token) return { status: 'skipped', reason: 'no-token' }

    const deviceId = await getDeviceId(db)

    const pushed = await pushDirtyRows(transport, deviceId, token, db)
    const { pulled, repaired } = await pullChanges(transport, deviceId, token, db)

    await setSyncState('lastSyncAt', String(Date.now()), db)
    await setSyncState('lastSyncError', '', db)

    return { status: 'ok', pushed, pulled, repaired }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err)
    const unauthorized = /\b401\b|unauthori[sz]ed|invalid token/i.test(message)

    logger.warn('sync', 'Sync failed', message)
    await setSyncState('lastSyncError', message, db)

    return { status: 'error', message, unauthorized }
  } finally {
    inFlight = false
  }
}

// -----------------------------------------------------------------------------
// Push
// -----------------------------------------------------------------------------

async function collectDirtyRows(db: BlockyTimeDB): Promise<DirtyRow[]> {
  const out: DirtyRow[] = []

  for (const table of LOCAL_TABLES) {
    const rows = await tableOf(db, table).where('_dirty').equals(1).toArray()
    for (const row of rows) {
      out.push({ table, row })
    }
  }

  return out
}

async function pushDirtyRows(
  transport: SyncTransport,
  deviceId: string,
  token: string,
  db: BlockyTimeDB,
): Promise<number> {
  const dirty = await collectDirtyRows(db)
  if (dirty.length === 0) return 0

  // §4.1: snapshot updatedAt alongside each id BEFORE the round trip. The
  // push takes hundreds of milliseconds; if the user marks a block during it,
  // clearing the flag unconditionally would mark that edit clean even though
  // the server only ever saw the previous version. It would then never sync
  // and never self-repair, because the §6 guard only protects dirty rows.
  const snapshot = new Map<string, number>()
  for (const { row } of dirty) snapshot.set(row.id, row.updatedAt)

  let pushed = 0

  for (let i = 0; i < dirty.length; i += PUSH_CHUNK_SIZE) {
    const chunk = dirty.slice(i, i + PUSH_CHUNK_SIZE)

    const changes: TableChanges = {}
    for (const { table, row } of chunk) {
      const sqlTable = toSqlTable(table)
      const list = changes[sqlTable] ?? []
      list.push(serializeRow(row))
      changes[sqlTable] = list
    }

    const response = await transport.push({ deviceId, changes }, token)
    await clearDirtyIfUnchanged(response, snapshot, db)
    pushed += chunk.length
  }

  return pushed
}

/** §4.1. Clears the flag only for rows that did not change mid-flight. */
async function clearDirtyIfUnchanged(
  response: PushResponse,
  snapshot: ReadonlyMap<string, number>,
  db: BlockyTimeDB,
): Promise<void> {
  for (const [sqlTable, ids] of Object.entries(response.acceptedIds ?? {})) {
    const local = toLocalTable(sqlTable as never)
    if (!local || !ids || ids.length === 0) continue

    await tableOf(db, local)
      .where('id')
      .anyOf(ids as string[])
      .modify((row) => {
        if (row.updatedAt === snapshot.get(row.id)) {
          row._dirty = 0
        }
        // Otherwise it was edited during the push. Leave it dirty; the next
        // cycle will send the newer version.
      })
  }
}

// -----------------------------------------------------------------------------
// Pull
// -----------------------------------------------------------------------------

async function pullChanges(
  transport: SyncTransport,
  deviceId: string,
  token: string,
  db: BlockyTimeDB,
): Promise<{ pulled: number; repaired: number }> {
  let cursor = await getLastPulledSeq(db)
  const touchedDayKeys = new Set<number>()

  let pulled = 0
  let pages = 0

  // Bounded so a server that always reports hasMore cannot spin forever.
  const MAX_PAGES = 1000

  for (;;) {
    // §2: the cursor is server_seq, NEVER updatedAt. updatedAt is a client
    // clock; a device whose clock ran slow would have its rows permanently
    // skipped by every other device.
    const page = await transport.pull(cursor, deviceId, token)

    pulled += await applyRemoteChanges(page.changes, touchedDayKeys, db)

    // Advance only after the page is durably applied. Interrupting here is
    // safe: the next sync refetches from the same cursor, and applying twice
    // is a no-op.
    if (page.nextCursor > cursor) {
      cursor = page.nextCursor
      await setLastPulledSeq(cursor, db)
    }

    pages += 1
    if (!page.hasMore || pages >= MAX_PAGES) break
  }

  const repaired = await repairSlotDuplicates([...touchedDayKeys], db)
  return { pulled, repaired }
}

async function applyRemoteChanges(
  changes: TableChanges,
  touchedDayKeys: Set<number>,
  db: BlockyTimeDB,
): Promise<number> {
  let applied = 0

  for (const [sqlTable, rows] of Object.entries(changes)) {
    const local = toLocalTable(sqlTable as never)
    if (!local || !rows) continue

    const target = tableOf(db, local)

    await db.transaction('rw', target, async () => {
      for (const remote of rows as ServerRow[]) {
        const existing = await target.get(remote.id)

        // §6: THE MOST IMPORTANT LINE IN THIS FILE.
        //
        // A dirty local row that is newer than the remote has not been pushed
        // yet. Overwriting it here is the classic "my edit vanished" bug: the
        // pull lands between the edit and its push, and the edit is gone with
        // no error and no way to recover it.
        if (existing?._dirty === 1 && existing.updatedAt >= remote.updated_at) continue

        await target.put(deserializeRow(remote) as AnyRow)
        applied += 1

        if (local === 'entries') {
          const dayKey = remote['day_key']
          if (typeof dayKey === 'number') touchedDayKeys.add(dayKey)
        }
      }
    })
  }

  return applied
}
